import { cn, formatCurrency, formatDate, getInitials } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Phone,
  Mail,
  MapPin,
  ShoppingCart,
  FileText,
  Package,
  Tag,
  Calendar,
} from 'lucide-react';
import { mockOrders } from '@/lib/mock-data';

export function CustomerPanel({ customer, conversation }) {
  if (!customer) return null;

  const customerOrders = mockOrders.filter((o) => o.customer === customer.name).slice(0, 3);

  return (
    <div className="h-full flex flex-col border-l border-border bg-card w-80">
      {/* Customer Info */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-col items-center text-center mb-4">
          <Avatar className="w-16 h-16 mb-3">
            <AvatarFallback className="bg-primary/10 text-primary text-xl">
              {getInitials(customer.name)}
            </AvatarFallback>
          </Avatar>
          <h3 className="font-semibold text-lg">{customer.name}</h3>
          <p className="text-sm text-muted-foreground">{customer.phone}</p>
        </div>

        <div className="flex flex-wrap gap-2 justify-center">
          {customer.tags?.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              <Tag className="w-3 h-3 mr-1" />
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      <ScrollArea className="flex-1">
        {/* Contact Details */}
        <div className="p-4 border-b border-border">
          <h4 className="text-sm font-medium mb-3">Contact Details</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span>{customer.phone}</span>
            </div>
            {customer.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{customer.email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="p-4 border-b border-border">
          <h4 className="text-sm font-medium mb-3">Customer Stats</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{customer.orders}</p>
              <p className="text-xs text-muted-foreground">Orders</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <p className="text-lg font-bold">{formatCurrency(customer.totalSpent)}</p>
              <p className="text-xs text-muted-foreground">Total Spent</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-b border-border">
          <h4 className="text-sm font-medium mb-3">Quick Actions</h4>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" size="sm" className="flex-col h-auto py-3">
              <ShoppingCart className="w-4 h-4 mb-1" />
              <span className="text-xs">Order</span>
            </Button>
            <Button variant="outline" size="sm" className="flex-col h-auto py-3">
              <Package className="w-4 h-4 mb-1" />
              <span className="text-xs">Product</span>
            </Button>
            <Button variant="outline" size="sm" className="flex-col h-auto py-3">
              <FileText className="w-4 h-4 mb-1" />
              <span className="text-xs">Invoice</span>
            </Button>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="p-4">
          <h4 className="text-sm font-medium mb-3">Recent Orders</h4>
          <div className="space-y-3">
            {customerOrders.length > 0 ? (
              customerOrders.map((order) => (
                <div
                  key={order.id}
                  className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{order.id}</span>
                    <Badge
                      variant="secondary"
                      className={cn(
                        'text-xs capitalize',
                        order.status === 'completed' && 'bg-green-100 text-green-800',
                        order.status === 'pending' && 'bg-yellow-100 text-yellow-800',
                        order.status === 'cancelled' && 'bg-red-100 text-red-800'
                      )}
                    >
                      {order.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{formatCurrency(order.amount)}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(order.date)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No orders yet
              </p>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
